import ApiClient from '@afosto/api/ApiClient';

class Hubs {
  constructor(client) {
    this.apiClient = client || ApiClient;
  }

  /**
   * Log a new hub error
   *
   * @param {string} id - Id of hub to add a log for
   * @param {Object} body - The new hub
   * @param {Object} options - The additional request options
   * @description Adds a new logged error for the hub
   * @returns {Promise}
   */
  addHubLog(id, body, options = {}) {
    return this.apiClient.request({
      url: `/spl/hubs/${id}/logs`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Create a new hub
   *
   * @param {Object} body - The new hub
   * @param {Object} options - The additional request options
   * @description Creates a new hub
   * @returns {Promise}
   */
  createHub(body, options = {}) {
    return this.apiClient.request({
      url: `/spl/hubs`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Deletes a hub
   *
   * @param {string} id - ID of hub to delete
   * @param {Object} options - The additional request options
   * @returns {Promise}
   */
  deleteHub(id, options = {}) {
    return this.apiClient.request({
      url: `/spl/hubs/${id}`,
      method: 'delete',
      ...options,
    });
  }

  /**
   * Get the hub
   *
   * @param {string} id - ID of hub to return
   * @param {Object} options - The additional request options
   * @description Returns the hub
   * @returns {Promise}
   */
  getHub(id, options = {}) {
    return this.apiClient.request({
      url: `/spl/hubs/${id}`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get the hub by uuid
   *
   * @param {string} id - The UUID of the hub to return
   * @param {Object} options - The additional request options
   * @description Returns the hub
   * @returns {Promise}
   */
  getHubByUuid(id, options = {}) {
    return this.apiClient.request({
      url: `/spl/hubs/${id}/uuid`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get the hub status
   *
   * @param {string} id - ID of hub to look for
   * @param {Object} options - The additional request options
   * @description Returns the hub information
   * @returns {Promise}
   */
  getHubStatus(id, options = {}) {
    return this.apiClient.request({
      url: `/spl/hubs/${id}/status`,
      method: 'get',
      ...options,
    });
  }

  /**
   * List the logs
   *
   * @param {Number} id - ID of hub to add a token for
   * @param {Object} options - The additional request options
   * @description Returns the hub log list
   * @returns {Promise}
   */
  listHubLogs(id, options = {}) {
    return this.apiClient.request({
      url: `/spl/hubs/${id}/logs`,
      method: 'get',
      ...options,
    });
  }

  /**
   * List the network information
   *
   * @param {Number} id - ID of hub to add a token for
   * @param {Object} options - The additional request options
   * @description Returns the hub network list
   * @returns {Promise}
   */
  listHubNetwork(id, options = {}) {
    return this.apiClient.request({
      url: `/spl/hubs/${id}/network`,
      method: 'get',
      ...options,
    });
  }

  /**
   * List the hubs
   *
   * @param {Object} options - The additional request options
   * @description Returns the hub list
   * @returns {Promise}
   */
  listHubs(options = {}) {
    return this.apiClient.request({
      url: `/spl/hubs`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Update a hub
   *
   * @param {string} id - ID of hub to update
   * @param {Object} body - The updated hub data
   * @param {Object} options - The additional request options
   * @description Update the hub to new hub settings
   * @returns {Promise}
   */
  updateHub(id, body, options = {}) {
    return this.apiClient.request({
      url: `/spl/hubs/${id}`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Set the network information
   *
   * @param {Number} id - ID of hub to update network information for
   * @param {Object} body - The new hub
   * @param {Object} options - The additional request options
   * @description Updates the hub network
   * @returns {Promise}
   */
  updateHubNetwork(id, body, options = {}) {
    return this.apiClient.request({
      url: `/spl/hubs/${id}/network`,
      method: 'put',
      data: body,
      ...options,
    });
  }
}

export const hubs = new Hubs();

export default Hubs;
